import { ModalPortal } from './ModalPortal';

import React, { useState, useEffect, useRef } from 'react';
import { useAuth, Role } from '../contexts/AuthContext';
import { mockBackend } from '../services/mockBackend';
import { Navigate } from 'react-router-dom';
import { Plus, Trash2, Edit2, CheckCircle2, XCircle, X, User, Image as ImageIcon, Camera } from 'lucide-react';

import { AlertModal } from './AlertModal';

const Users: React.FC = () => {
  const { user } = useAuth();
  const [usersList, setUsersList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', username: '', password: '', role: 'collaborator' as Role, active: true, photoUrl: '' });
  
  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await mockBackend.getUsers(user!);
      setUsersList(data);
    } finally { setIsLoading(false); }
  };

  const handleOpenModal = (userToEdit: any = null) => {
    setEditingUser(userToEdit);
    setFormData(userToEdit 
      ? { ...userToEdit, password: '', photoUrl: userToEdit.photoUrl || '' } 
      : { name: '', username: '', password: '', role: 'collaborator', active: true, photoUrl: '' }
    );
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        if (formData.password) await mockBackend.changePassword(user!, editingUser.id, formData.password);
        await mockBackend.updateUser(user!, editingUser.id, { 
            name: formData.name, 
            role: formData.role, 
            active: formData.active,
            photoUrl: formData.photoUrl 
        });
      } else {
        await mockBackend.createUser(user!, formData);
      }
      await loadUsers();
      setIsModalOpen(false);
    } catch (err: any) { showAlert(err.message); }
  };

  const handleDelete = async (targetId: string) => {
    if (!confirm('Confirmar exclusão?')) return;
    try {
      await mockBackend.deleteUser(user!, targetId);
      setUsersList(prev => prev.filter(u => u.id !== targetId));
    } catch (err: any) { showAlert(err.message); }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result as string;
        const uploadResult = await mockBackend.uploadImage(base64, file.name, file.type);
        setFormData(prev => ({ ...prev, photoUrl: uploadResult.url }));
      } catch (err: any) {
        showAlert("Erro no upload: " + err.message);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="font-sans animate-in fade-in duration-500">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      <div className="flex justify-end mb-4">
           <button onClick={() => handleOpenModal()} className="bg-brand-700 text-white px-4 py-2 font-bold uppercase hover:bg-brand-800 flex items-center gap-2 rounded shadow-sm text-xs transition-colors">
             <Plus size={16} /> Novo Usuário
           </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-sm rounded-lg overflow-x-auto">
           <table className="w-full text-left border-collapse">
             <thead>
               <tr className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                 <th className="px-5 py-3 font-semibold">Nome / Login</th>
                 <th className="px-5 py-3 font-semibold">Função</th>
                 <th className="px-5 py-3 font-semibold">Status</th>
                 <th className="px-5 py-3 font-semibold text-right">Ações</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-slate-100 text-sm">
               {usersList.map(u => (
                 <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                   <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 overflow-hidden flex items-center justify-center text-slate-400 shrink-0">
                              {u.photoUrl ? (
                                <img src={u.photoUrl} alt={u.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display='none'; }} />
                              ) : (
                                <User size={18}/>
                              )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 dark:text-slate-200">{u.name}</div>
                            <div className="text-xs text-slate-500">@{u.username}</div>
                          </div>
                      </div>
                   </td>
                   <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${
                        u.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 
                        u.role === 'celular' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                        'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                         {u.role === 'admin' ? 'Administrador' : u.role === 'celular' ? 'Celular' : 'Colaborador'}
                      </span>
                   </td>
                   <td className="px-5 py-3">
                      {u.active 
                         ? <span className="flex items-center gap-1 text-green-700 font-bold text-xs"><CheckCircle2 size={14}/> Ativo</span> 
                         : <span className="flex items-center gap-1 text-slate-400 font-bold text-xs"><XCircle size={14}/> Inativo</span>}
                   </td>
                   <td className="px-5 py-3 text-right flex justify-end gap-2">
                      <button onClick={() => handleOpenModal(u)} className="text-slate-400 hover:text-brand-600 p-1.5"><Edit2 size={16} /></button>
                      {u.id !== user?.id && (
                         <button onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                      )}
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
      </div>

      {isModalOpen && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/60 dark:bg-black/80 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
           <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-xl w-full max-w-md rounded-lg">
              <div className="bg-brand-900 dark:bg-brand-950 text-white px-6 py-3 flex justify-between items-center rounded-t-lg">
                 <h3 className="font-bold text-sm uppercase">{editingUser ? 'Editar' : 'Criar'} Usuário</h3>
                 <button onClick={() => setIsModalOpen(false)}><X size={20}/></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                 <div className="flex gap-4 items-start">
                     {/* Photo Preview in Modal */}
                     <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 flex items-center justify-center overflow-hidden shrink-0 text-slate-400">
                        {formData.photoUrl ? (
                            <img src={formData.photoUrl} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                            <User size={32} />
                        )}
                     </div>
                     <div className="flex-1 space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Nome</label>
                            <input 
                              type="text" 
                              required 
                              value={formData.name} 
                              onChange={e => setFormData({...formData, name: e.target.value})} 
                              className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500" 
                              placeholder="Ex: Maria Oliveira"
                            />
                        </div>
                     </div>
                 </div>

                 <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Foto (URL ou Upload)</label>
                    <div className="flex items-center border border-slate-300 dark:border-slate-700 rounded overflow-hidden">
                        <div className="pl-3 pr-2 text-slate-400 bg-slate-50 dark:bg-slate-800 h-full py-2 border-r border-slate-200 dark:border-slate-700"><ImageIcon size={16}/></div>
                        <input type="text" value={formData.photoUrl} onChange={e => setFormData({...formData, photoUrl: e.target.value})} className="w-full p-2 text-sm focus:outline-none bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200" placeholder="https://..." />
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="px-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border-l border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 h-full flex items-center">
                            {isUploading ? '...' : <Camera size={16} />}
                        </button>
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
                 </div>

                 <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Login</label>
                    <input 
                      type="text" 
                      required 
                      disabled={!!editingUser} 
                      value={formData.username} 
                      onChange={e => setFormData({...formData, username: e.target.value})} 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500" 
                      placeholder="Ex: maria.admin"
                    />
                 </div>
                 <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">{editingUser ? 'Nova Senha (Opcional)' : 'Senha'}</label>
                    <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500" placeholder="••••••" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Função</label>
                       <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as Role})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500">
                         <option value="collaborator">Colaborador</option>
                         <option value="admin">Admin</option>
                         <option value="celular">Celular</option>
                       </select>
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Status</label>
                       <select value={formData.active ? 'true' : 'false'} onChange={e => setFormData({...formData, active: e.target.value === 'true'})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500">
                         <option value="true">Ativo</option>
                         <option value="false">Inativo</option>
                       </select>
                    </div>
                 </div>
                 <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 mt-4">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors">Cancelar</button>
                    <button type="submit" disabled={isUploading} className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded shadow-sm disabled:opacity-50">Salvar</button>
                 </div>
              </form>
           </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default Users;
